import { REACT_APP_API_URL } from "../../../env";

export const getApiBaseUrl = () => {
  const url = REACT_APP_API_URL || "http://localhost:5000";
  return url.replace(/\/+$/, "");
};

export const buildFullApiUrl = (portalId) => {
  const base = getApiBaseUrl();
  const endpoint = "/api/visitor/portal-clicks";
  if (!portalId) {
    return `${base}${endpoint}`;
  }
  return `${base}${endpoint}?portalId=${encodeURIComponent(portalId)}`;
};

const formatItem = (item, index) => {
  return {
    id: item.id || item._id || item.portalId || `portal-${index}`,
    portalId: item.portalId || item.portal_id || "N/A",
    count: typeof item.count === "number" ? item.count : Number(item.count || 0),
    lastUpdated: item.lastUpdated || item.updatedAt || item.timestamp || null,
    success: item.success ?? true,
    details: item,
  };
};

export const normalizePortalClickData = (responseData, defaultPortalId = "dalma_wildlife") => {
  if (!responseData) return [];

  // Case 1: portals array present in response object (Aggregate response)
  if (Array.isArray(responseData.portals)) {
    return responseData.portals.map((item, index) => formatItem(item, index));
  }

  // Case 2: Directly an array of portal items
  if (Array.isArray(responseData)) {
    return responseData.map((item, index) => formatItem(item, index));
  }

  // Case 3: Nested data array
  if (Array.isArray(responseData.data)) {
    return responseData.data.map((item, index) => formatItem(item, index));
  }

  // Case 4: portalMap present (dictionary object mapping portalId -> count)
  if (responseData.portalMap && typeof responseData.portalMap === "object") {
    return Object.entries(responseData.portalMap).map(([portalId, count], index) => ({
      id: portalId || `portal-${index}`,
      portalId,
      count: typeof count === "number" ? count : Number(count || 0),
      lastUpdated: responseData.lastUpdated || null,
      success: responseData.success ?? true,
      details: { portalId, count },
    }));
  }

  // Case 5: Single portal object inside data field
  if (responseData.data && typeof responseData.data === "object" && responseData.data.portalId) {
    return [formatItem(responseData.data, 0)];
  }

  // Case 6: Single portal response directly at top-level
  if (responseData.portalId !== undefined || responseData.count !== undefined) {
    return [formatItem(responseData, 0)];
  }

  // Case 7: Fallback success object with count
  if (responseData.success && responseData.count !== undefined) {
    return [
      {
        id: "1",
        portalId: defaultPortalId,
        count: Number(responseData.count || 0),
        lastUpdated: responseData.lastUpdated || null,
        success: true,
      },
    ];
  }

  return [];
};

export const extractTotalClicks = (responseData) => {
  if (!responseData) return 0;
  if (responseData.totalClicks !== undefined && responseData.totalClicks !== null) {
    return Number(responseData.totalClicks || 0);
  }
  
  const portals = normalizePortalClickData(responseData);
  if (portals.length > 0) {
    return portals.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  }

  if (responseData.count !== undefined && responseData.count !== null) {
    return Number(responseData.count || 0);
  }

  return 0;
};

export const extractPortalsList = (responseData) => {
  return normalizePortalClickData(responseData);
};

export const extractLatestUpdated = (responseData) => {
  if (!responseData) return null;
  if (responseData.lastUpdated) return responseData.lastUpdated;

  const portals = extractPortalsList(responseData);
  let latest = null;

  for (const portal of portals) {
    if (portal.lastUpdated) {
      if (!latest || new Date(portal.lastUpdated) > new Date(latest)) {
        latest = portal.lastUpdated;
      }
    }
  }

  return latest;
};

export const extractTopPortal = (responseData) => {
  const portals = extractPortalsList(responseData);
  if (!portals || portals.length === 0) return null;

  return portals.reduce((max, current) => {
    return (current.count || 0) > (max.count || 0) ? current : max;
  }, portals[0]);
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return String(dateString);
  }
};

export const getRelativeTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch (e) {
    return "";
  }
};

export const formatPortalName = (portalId) => {
  if (!portalId) return "Unknown Portal";
  return portalId
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

